<template>
  <div>
    <back-button default-to="/crafts"/>
    <b-card no-header>
      <div class="d-flex flex-wrap align-items-center mb-3">
        <div>
          <h5 class="mb-1">{{ transaction_no }}</h5>
          <small class="text-muted">{{ workflowLabel(workflowStatus) }}</small>
        </div>
        <div class="ml-auto d-flex align-items-center flex-wrap">
          <b-badge class="mr-2" :variant="statusVariant(approvalStatus)">{{ statusLabel(approvalStatus) }}</b-badge>
          <b-badge :variant="workflowVariant(workflowStatus)">{{ workflowLabel(workflowStatus) }}</b-badge>
          <b-button v-if="isApproved" class="ml-2" variant="primary" :href="`${apiUrl.craft}/${$route.params.id}?print_pdf=1&access_token=${$store.getters['account/accessToken']}`" target="_blank">
            <font-awesome-icon icon="file-pdf"/> Cetak Surat Tempahan
          </b-button>
        </div>
      </div>

      <b-alert :show="approvalStatus === 'returned' && !!reviewNote" variant="warning">Dikembalikan untuk diperbaiki: {{ reviewNote }}</b-alert>
      <b-alert :show="approvalStatus === 'rejected' && !!reviewNote" variant="danger">Ditolak: {{ reviewNote }}</b-alert>
      <b-alert :show="isInitialReview" variant="info">Tempahan awal sedang menunggu persetujuan owner. Data akhir dapat diisi setelah tempahan disetujui.</b-alert>
      <b-alert :show="isFinalReview" variant="info">Data akhir sedang menunggu persetujuan owner. Stok dan barcode belum dibuat.</b-alert>
      <b-alert :show="isOwnReview" variant="warning">Anda adalah pengaju data ini. Persetujuan harus dilakukan oleh owner/admin lain.</b-alert>
      <b-alert :show="isStocked" variant="success">Data akhir sudah disetujui dan barang sudah masuk stok.</b-alert>

      <b-row>
        <b-col cols="12" md="6" class="mb-3">
          <strong>Pelanggan</strong>
          <div v-if="costumer" class="mt-1">{{ costumer.nama }}<br><small class="text-muted">{{ costumer.no_hp }}<span v-if="costumer.nik"> · NIK {{ costumer.nik }}</span></small></div>
        </b-col>
        <b-col cols="6" md="3" class="mb-3"><strong>Tanggal tempahan</strong><div>{{ formatDate(model.tanggal_transaksi.value) }}</div></b-col>
        <b-col cols="6" md="3" class="mb-3"><strong>Tanggal diambil</strong><div>{{ formatDate(model.tanggal_diambil.value) }}</div></b-col>
      </b-row>

      <div class="table-responsive mb-3">
        <table class="table table-striped align-middle">
          <thead><tr><th>Barang Tempahan</th><th>Ukuran</th><th>Berat</th><th>Harga</th><th>Data Stok Akhir</th></tr></thead>
          <tbody>
            <tr v-for="item in details" :key="item.id">
              <td><strong>{{ item.uraian }}</strong><small v-if="item.final && item.final.foto" class="d-block text-muted">Foto akhir sudah tersedia</small></td>
              <td>{{ item.size || '-' }}</td>
              <td>{{ item.berat || 0 }} gram</td>
              <td>Rp{{ $formatPrice(item.harga) }}</td>
              <td>
                <template v-if="item.final">
                  <strong>{{ item.final.nama }}</strong>
                  <small class="d-block text-muted">{{ typeLabel(item.final.jenis_barang) }} · {{ item.final.berat }} {{ item.final.jenis_barang === 'emas' ? 'gram' : 'buah' }}</small>
                  <small v-if="item.final.stock_barcode" class="d-block text-success">Barcode: {{ item.final.stock_barcode }}</small>
                </template>
                <span v-else class="text-muted">Belum diisi</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="canEditFinal" class="mb-3 p-3 bg-light rounded">
        <div class="d-flex flex-wrap align-items-center">
          <div><strong>Barang sudah selesai dikerjakan?</strong><div class="text-muted small">Upload foto dan data akhir sebelum barang dimasukkan ke stok.</div></div>
          <b-button class="ml-auto mt-2 mt-md-0" variant="primary" @click="openFinalModal">{{ hasFinalData ? 'Ubah Data Akhir' : 'Input Data Akhir' }}</b-button>
        </div>
      </div>

      <div v-if="canReview" class="mb-3">
        <b-button variant="success" :disabled="isLoading" @click="approve">Setujui</b-button>
        <b-button variant="warning" class="ml-2" :disabled="isLoading" @click="showDecline('return')">Kembalikan</b-button>
        <b-button variant="danger" class="ml-2" :disabled="isLoading" @click="showDecline('reject')">Tolak</b-button>
      </div>

      <div class="d-flex flex-wrap">
        <b-button v-if="canEdit" :to="'/crafts/' + $route.params.id + '/edit'" variant="primary">Ubah Tempahan</b-button>
        <b-button v-if="canEdit" variant="danger" class="ml-2" :disabled="isLoading" @click="removeDraft">Hapus Draft</b-button>
        <b-button to="/crafts" variant="secondary" class="ml-auto">Kembali</b-button>
      </div>
    </b-card>

    <b-modal ref="finalModal" title="Data Akhir Barang" size="xl" hide-footer>
      <b-alert show variant="info" class="small">Data ini akan menjadi data stok dan digunakan untuk membuat barcode. Stok belum berubah sampai owner menyetujui pengajuan.</b-alert>
      <div v-for="(item, index) in finalDetails" :key="item.detail_id" class="final-item border rounded p-3 mb-3">
        <div class="d-flex justify-content-between mb-2"><strong>Barang {{ index + 1 }}</strong><span class="text-muted small">{{ details[index] ? details[index].uraian : '' }}</span></div>
        <b-row>
          <b-col cols="12" md="4"><b-form-group label="Nama barang"><b-form-input v-model="item.nama" /></b-form-group></b-col>
          <b-col cols="12" md="3"><b-form-group label="Jenis barang"><b-form-select v-model="item.jenis_barang" :options="jenisOptions" /></b-form-group></b-col>
          <b-col cols="6" md="2"><b-form-group label="Berat"><b-form-input v-model="item.berat" type="number" step="0.001" /></b-form-group></b-col>
          <b-col cols="6" md="3"><b-form-group label="Kadar"><b-form-input v-model="item.kadar" :disabled="item.jenis_barang === 'berlian'" /></b-form-group></b-col>
          <b-col cols="12" md="4"><b-form-group label="Harga modal"><b-form-input v-model="item.harga_modal" type="number" /></b-form-group></b-col>
          <b-col cols="12" md="4"><b-form-group label="Harga jual"><b-form-input v-model="item.harga_jual" type="number" /></b-form-group></b-col>
          <b-col cols="12" md="4"><b-form-group label="Foto akhir"><b-form-file accept="image/*" browse-text="Pilih foto" placeholder="Belum ada foto" @change="onFinalPhoto($event, index)"/><small v-if="item.foto_url" class="text-success d-block mt-1">Foto sebelumnya tersedia</small></b-form-group></b-col>
        </b-row>
      </div>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" @click="$refs.finalModal.hide()">Batal</b-button>
        <b-button class="ml-2" variant="outline-primary" :disabled="isLoading" @click="saveFinal(false)">Simpan Data Akhir</b-button>
        <b-button class="ml-2" variant="primary" :disabled="isLoading" @click="saveFinal(true)">Simpan & Ajukan Approval</b-button>
      </div>
    </b-modal>

    <b-modal ref="declineModal" :title="declineMode === 'return' ? 'Kembalikan untuk Diperbaiki' : 'Tolak Tempahan'" :ok-title="declineMode === 'return' ? 'Kembalikan' : 'Tolak'" cancel-title="Batal" @ok="decline">
      <b-form-textarea v-model="declineNote" placeholder="Wajib diisi untuk pengembalian atau penolakan" />
    </b-modal>
  </div>
</template>

<script>
import { setModelValue, Roles } from '@/_helpers'
import Craft from '@/models/Craft.js'

const craftModel = new Craft()

export default {
  data () {
    return {
      Roles,
      isLoading: false,
      transaction_no: null,
      approvalStatus: null,
      workflowStatus: null,
      reviewNote: null,
      createdBy: null,
      submittedBy: null,
      finalSubmittedBy: null,
      costumer: null,
      details: [],
      finalDetails: [],
      declineMode: 'return',
      declineNote: '',
      model: craftModel.init(),
      apiUrl: { craft: craftModel.endpoint },
      jenisOptions: [
        { value: 'emas', text: 'Emas' },
        { value: 'berlian', text: 'Berlian' },
        { value: 'perhiasan', text: 'Perhiasan' }
      ]
    }
  },
  computed: {
    isAdmin () { return this.$store.getters['account/accountData'].role === Roles.ADMIN },
    isApproved () { return this.approvalStatus === 'approved' },
    isInitialReview () { return this.approvalStatus === 'on_review' && this.workflowStatus === 'menunggu_pengerjaan' },
    isFinalReview () { return this.workflowStatus === 'data_akhir_on_review' },
    isStocked () { return ['sudah_masuk_stok', 'sudah_diambil'].indexOf(this.workflowStatus) !== -1 },
    reviewRequesterId () { return this.isFinalReview ? this.finalSubmittedBy : (this.submittedBy || this.createdBy) },
    isOwnReview () { return this.isAdmin && this.approvalStatus === 'on_review' && Number(this.reviewRequesterId) === Number(this.$store.getters['account/accountData'].id) },
    canReview () { return this.isAdmin && this.approvalStatus === 'on_review' && !this.isOwnReview },
    canEditFinal () { return (this.isCreator || this.isAdmin) && this.approvalStatus !== 'on_review' && ['menunggu_pengerjaan', 'data_akhir_draft'].indexOf(this.workflowStatus) !== -1 },
    hasFinalData () { return this.details.some(item => item.final) },
    isCreator () { return Number(this.createdBy) === Number(this.$store.getters['account/accountData'].id) },
    canEdit () { return this.isCreator && ['draft', 'returned'].indexOf(this.approvalStatus) !== -1 }
  },
  mounted () { this.getDetail() },
  methods: {
    statusLabel (status) { return { draft: 'Draft', on_review: 'On Review', approved: 'Disetujui', returned: 'Dikembalikan', rejected: 'Ditolak' }[status] || status },
    statusVariant (status) { return { draft: 'secondary', on_review: 'warning', approved: 'success', returned: 'warning', rejected: 'danger' }[status] || 'secondary' },
    workflowLabel (status) { return { draft: 'Draft tempahan', menunggu_pengerjaan: 'Menunggu pengerjaan', data_akhir_draft: 'Data akhir siap diperbaiki', data_akhir_on_review: 'Data akhir menunggu approval', sudah_masuk_stok: 'Sudah masuk stok', sudah_diambil: 'Sudah diambil' }[status] || status || '-' },
    workflowVariant (status) { return { menunggu_pengerjaan: 'info', data_akhir_draft: 'warning', data_akhir_on_review: 'warning', sudah_masuk_stok: 'success', sudah_diambil: 'secondary' }[status] || 'secondary' },
    typeLabel (value) { return { emas: 'Emas', berlian: 'Berlian', perhiasan: 'Perhiasan' }[value] || value || '-' },
    formatDate (value) { return value ? this.$moment(value).format('DD/MM/YYYY') : '-' },
    async getDetail () {
      this.isLoading = true
      try {
        const resp = await craftModel.find(this.$route.params.id)
        this.costumer = resp.costumer
        this.details = resp.detail_barang || []
        this.finalDetails = this.details.map(item => {
          const final = item.final || {}
          return { detail_id: item.id, nama: final.nama || item.uraian || '', jenis_barang: final.jenis_barang || 'emas', berat: final.berat || item.berat || '', kadar: final.kadar || '', harga_modal: final.harga_modal || item.harga || 0, harga_jual: final.harga_jual || item.harga || 0, foto_url: final.foto || '', foto: null }
        })
        this.transaction_no = resp.transaction_no
        this.approvalStatus = resp.approval_status
        this.workflowStatus = resp.workflow_status || (resp.approval_status === 'approved' ? 'sudah_diambil' : 'draft')
        this.reviewNote = resp.final_review_note || resp.review_note
        this.createdBy = resp.created_by
        this.submittedBy = resp.submitted_by
        this.finalSubmittedBy = resp.final_submitted_by
        setModelValue(this.model, resp)
      } catch (e) { this.$store.dispatch('notification/error', e) }
      this.isLoading = false
    },
    openFinalModal () { this.$refs.finalModal.show() },
    onFinalPhoto (value, index) {
      const files = value && value.target ? value.target.files : value
      this.finalDetails[index].foto = files instanceof File ? files : (files && files.length ? files[0] : null)
    },
    finalPayload () { return this.finalDetails.map(item => ({ detail_id: item.detail_id, nama: item.nama, jenis_barang: item.jenis_barang, berat: item.berat, kadar: item.kadar, harga_modal: item.harga_modal, harga_jual: item.harga_jual, foto: item.foto || undefined })) },
    validateFinal () {
      if (this.finalDetails.some(item => !item.nama || Number(item.berat) <= 0 || (!item.foto && !item.foto_url))) {
        this.$store.dispatch('notification/error', 'Lengkapi nama, berat, dan foto akhir untuk semua barang.')
        return false
      }
      return true
    },
    async saveFinal (submit) {
      if (!this.validateFinal()) return
      this.isLoading = true
      try {
        await craftModel.saveFinalData(this.$route.params.id, this.finalPayload())
        if (submit) {
          await craftModel.submitFinalData(this.$route.params.id)
          this.$store.dispatch('notification/success', 'Data akhir berhasil diajukan untuk persetujuan owner.')
        } else this.$store.dispatch('notification/success', 'Data akhir berhasil disimpan sebagai draft.')
        this.$refs.finalModal.hide()
        await this.getDetail()
      } catch (e) { this.$store.dispatch('notification/error', e) }
      this.isLoading = false
    },
    async approve () { await this.runAction(() => craftModel.approve(this.$route.params.id), this.isFinalReview ? 'Data akhir disetujui dan barang masuk stok.' : 'Tempahan berhasil disetujui.') },
    showDecline (mode) { this.declineMode = mode; this.declineNote = ''; this.$refs.declineModal.show() },
    async decline (event) {
      if (!this.declineNote) { event.preventDefault(); this.$store.dispatch('notification/error', 'Catatan alasan wajib diisi.'); return }
      const action = this.declineMode === 'return' ? craftModel.returnForRevision(this.$route.params.id, this.declineNote) : craftModel.reject(this.$route.params.id, this.declineNote)
      await this.runAction(() => action, this.declineMode === 'return' ? 'Data dikembalikan untuk diperbaiki.' : 'Data ditolak.')
    },
    async runAction (action, message) { this.isLoading = true; try { await action(); this.$store.dispatch('notification/success', message); await this.getDetail() } catch (e) { this.$store.dispatch('notification/error', e) } this.isLoading = false },
    async removeDraft () {
      if (!window.confirm('Hapus draft tempahan ini?')) return
      await this.runAction(() => craftModel.delete(this.$route.params.id), 'Draft tempahan dihapus.')
      this.$router.push('/crafts')
    }
  }
}
</script>

<style scoped>
.final-item { background: #fafbfc; }
</style>
